/**
 * Pure composer + parser for the `[MEDIA_UPLOAD: ...]` agent-context block.
 *
 * Single source of truth for the wire format shared across:
 *   - useChat.sendMessage (legacy chat attachments)
 *   - useChat.sendSopFiles (SOP ingestion drop)
 *   - persisted synthetic user bubble body
 *   - MessageBubble strip-for-display path
 *
 * Pure: no fs, no React, no Convex. Safe to import from the Convex bundle.
 */

export interface MediaUploadRef {
  readonly type: string;
  readonly name: string;
  readonly path: string;
  readonly size: number;
}

export function composeMediaUploadBlock(ref: MediaUploadRef): string {
  return `[MEDIA_UPLOAD: type=${ref.type} | name=${ref.name} | path=${ref.path} | size=${ref.size}]`;
}

export function composeMediaUploadBlocks(
  refs: readonly MediaUploadRef[],
): string {
  return refs.map(composeMediaUploadBlock).join("\n");
}

const HEADER_RE = /^\[MEDIA_UPLOAD:\s*(.+?)\]\s*\n?/;

export function parseMediaUploadBlock(content: string): {
  rest: string;
  meta: MediaUploadRef | null;
} {
  const match = content.match(HEADER_RE);
  if (!match) return { rest: content, meta: null };
  const pairs = match[1].split("|").map((s) => s.trim());
  const obj: Record<string, string> = {};
  for (const p of pairs) {
    const eq = p.indexOf("=");
    if (eq === -1) continue;
    obj[p.slice(0, eq).trim()] = p.slice(eq + 1).trim();
  }
  if (!obj.type || !obj.name || !obj.path || !obj.size) {
    return { rest: content, meta: null };
  }
  return {
    rest: content.slice(match[0].length),
    meta: {
      type: obj.type,
      name: obj.name,
      path: obj.path,
      size: Number(obj.size),
    },
  };
}

export function parseAllMediaUploadBlocks(content: string): {
  rest: string;
  metas: MediaUploadRef[];
} {
  const metas: MediaUploadRef[] = [];
  let cursor = content;
  while (true) {
    const { rest, meta } = parseMediaUploadBlock(cursor);
    if (!meta) break;
    metas.push(meta);
    cursor = rest;
  }
  return { rest: cursor, metas };
}
